<template>
  <div v-if="visible" class="cr-modal-fondo">
    <div class="cr-modal">
      <div class="cr-modal-header">
        <h5 v-if="modoCliente" class="mb-0">Cambiar cliente</h5>
        <h5 v-else class="mb-0">
          Nueva cotización<span v-if="tipoFijo"> de {{ form.tipo === TIPO_FACTURA ? "factura" : "requerimiento" }}</span>
        </h5>
        <button type="button" class="close" @click="cerrar" title="Cerrar">
          <span>&times;</span>
        </button>
      </div>

      <div class="cr-modal-body">
        <!-- Tipo de cotizacion: solo se elige si la pantalla no lo define (menu POS o Inventario) -->
        <label v-if="!tipoFijo" class="cr-label">Tipo</label>
        <div v-if="!tipoFijo" class="cr-tipos">
          <button type="button" class="btn" :class="form.tipo === TIPO_FACTURA ? 'btn-primary' : 'btn-outline-primary'"
            @click="seleccionarTipo(TIPO_FACTURA)">
            <i class="fas fa-file-invoice-dollar"></i>
            Factura
          </button>
          <button type="button" class="btn"
            :class="form.tipo === TIPO_REQUERIMIENTO ? 'btn-primary' : 'btn-outline-primary'"
            @click="seleccionarTipo(TIPO_REQUERIMIENTO)">
            <i class="fas fa-truck-loading"></i>
            Requerimiento
          </button>
        </div>

        <!-- En modo "cambiar cliente" solo se muestra la seleccion de cliente -->
        <div v-if="!modoCliente" class="row" :class="{ 'mt-3': !tipoFijo }">
          <div class="col-12" :class="{ 'col-md-6': form.tipo === TIPO_FACTURA }">
            <div class="form-group">
              <label class="cr-label" :class="{ 'text-danger': errores.tienda }">Tienda</label>
              <select class="form-control" v-model="form.tienda" @change="onCambioTienda">
                <option :value="null" disabled>Seleccione</option>
                <option v-for="tienda in tiendas" :key="tienda.codigo" :value="tienda.codigo">
                  {{ tienda.codigo }} - {{ tienda.nombre }}
                </option>
              </select>
              <small v-if="errores.tienda" class="form-text text-danger">{{ errores.tienda }}</small>
            </div>
          </div>
          <template v-if="form.tipo === TIPO_FACTURA">
            <div class="col-7 col-md-3">
              <div class="form-group">
                <label class="cr-label" :class="{ 'text-danger': errores.caja }">Caja</label>
                <select class="form-control" v-model="form.caja" :disabled="!form.tienda">
                  <option :value="null" disabled>Seleccione</option>
                  <option v-for="caja in cajas" :key="caja.id" :value="caja.id" :disabled="!caja.esta_abierta">
                    {{ caja.caja }}{{ caja.esta_abierta ? "" : " (cerrada)" }}
                  </option>
                </select>
                <small v-if="errores.caja" class="form-text text-danger">{{ errores.caja }}</small>
              </div>
            </div>
            <div class="col-5 col-md-3">
              <div class="form-group">
                <label class="cr-label">Pago</label>
                <select class="form-control" v-model="form.tipo_pago">
                  <option value="CONTADO">Contado</option>
                  <option value="CREDITO">Crédito</option>
                </select>
              </div>
            </div>
          </template>
        </div>

        <!-- Cliente -->
        <label class="cr-label" :class="{ 'text-danger': errores.cliente }">
          {{ esRequerimiento ? "Supercliente" : "Cliente" }}
        </label>

        <div v-if="clienteSeleccionado" class="cr-cliente-sel">
          <div>
            <b>{{ cliente.nit }}</b>
            <span v-if="esRequerimiento && cliente.codigo" class="badge badge-secondary ml-1">{{ cliente.codigo }}</span>
            <span v-if="clienteNuevo" class="badge badge-info ml-1">Nuevo</span>
            <div>{{ cliente.nombre }}</div>
            <small class="text-muted">{{ cliente.direccion }}</small>
          </div>
          <button type="button" class="btn btn-sm btn-outline-secondary ml-2" @click="cambiarCliente">
            Cambiar
          </button>
        </div>

        <template v-else-if="!clienteNuevo">
          <div class="input-group">
            <input ref="inputBuscarCliente" v-model="busquedaCliente" class="form-control"
              :placeholder="esRequerimiento ? 'Código, nombre o NIT del supercliente' : 'NIT o nombre del cliente'" type="text" autocomplete="off" @keyup.enter="buscarCliente" />
            <div class="input-group-append">
              <button type="button" class="btn btn-dark" @click="buscarCliente" :disabled="buscando">
                <i class="fa fa-search"></i>
              </button>
            </div>
          </div>
          <div v-if="buscando" class="small text-muted mt-2">Buscando...</div>
          <div v-else-if="resultadosCliente.length > 0" class="cr-resultados">
            <button v-for="item in resultadosCliente" :key="item.codigo || item.nit" type="button" class="cr-resultado"
              @click="seleccionarCliente(item)">
              <template v-if="esRequerimiento">
                <b>{{ item.codigo }}</b> - {{ item.nombre }}
                <small class="d-block text-muted">NIT: {{ item.nit || "-" }} | {{ item.direccion }}</small>
              </template>
              <template v-else>
                <b>{{ item.nit }}</b> - {{ item.nombre }}
                <small class="d-block text-muted">{{ item.direccion }}</small>
              </template>
            </button>
          </div>
          <div v-else-if="buscado" class="small text-muted mt-2">
            No se encontraron clientes con "{{ ultimoTextoBuscado }}".
          </div>
          <button type="button" class="btn btn-link px-0 mt-1" @click="ingresarClienteNuevo">
            <i class="fa fa-user-plus"></i>
            {{ esRequerimiento ? "Ingresar supercliente nuevo" : "Ingresar cliente nuevo" }}
          </button>
        </template>

        <template v-else>
          <div class="row">
            <div class="col-12 col-md-4">
              <div class="form-group">
                <label class="cr-label" :class="{ 'text-danger': errores.nit }">NIT</label>
                <input v-model="cliente.nit" class="form-control" type="text" />
                <small v-if="errores.nit" class="form-text text-danger">{{ errores.nit }}</small>
              </div>
            </div>
            <div class="col-12 col-md-8">
              <div class="form-group">
                <label class="cr-label" :class="{ 'text-danger': errores.nombre }">Nombre</label>
                <input v-model="cliente.nombre" class="form-control" type="text" />
                <small v-if="errores.nombre" class="form-text text-danger">{{ errores.nombre }}</small>
              </div>
            </div>
            <div class="col-12">
              <div class="form-group">
                <label class="cr-label" :class="{ 'text-danger': errores.direccion }">Dirección</label>
                <input v-model="cliente.direccion" class="form-control" type="text" />
                <small v-if="errores.direccion" class="form-text text-danger">{{ errores.direccion }}</small>
              </div>
            </div>
            <div class="col-12">
              <div class="form-group">
                <label class="cr-label">Correo electrónico</label>
                <input v-model="cliente.email" class="form-control" type="email" />
              </div>
            </div>
          </div>
          <small v-if="form.tipo === TIPO_FACTURA" class="form-text text-muted mb-2">
            Para factura el cliente se registra en el catálogo de clientes.
          </small>
          <small v-if="esRequerimiento" class="form-text text-muted mb-2">
            El supercliente se registra en el catálogo de superclientes de la tienda {{ form.tienda }}.
          </small>
          <button type="button" class="btn btn-link px-0" @click="cancelarClienteNuevo">
            <i class="fa fa-search"></i>
            Buscar cliente existente
          </button>
        </template>

        <div v-if="errores.detail" class="alert alert-danger mt-3 mb-0 py-2">{{ errores.detail }}</div>
      </div>

      <div class="cr-modal-footer">
        <button type="button" class="btn btn-secondary" @click="cerrar">Cancelar</button>
        <button v-if="modoCliente" type="button" class="btn btn-success" @click="guardarCliente"
          :disabled="guardando">
          <i class="fa fa-user-check"></i>
          {{ guardando ? "Guardando..." : "Guardar cliente" }}
        </button>
        <button v-else type="button" class="btn btn-success" @click="crear" :disabled="guardando">
          <i class="fa fa-barcode"></i>
          {{ guardando ? "Creando..." : "Continuar" }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import Notifications from "@/notifications/notifications";
import CotizacionMovilService, {
  TIPO_FACTURA,
  TIPO_REQUERIMIENTO,
} from "@/services/cxc/CotizacionMovilService";

function clienteVacio() {
  // codigo: supercliente (solo cotizacion de requerimiento)
  return { codigo: "", nit: "", nombre: "", direccion: "", email: "" };
}

/**
 * Modal inicial de la captura movil: tipo de cotizacion, tienda (y caja/pago
 * para factura) y cliente. Al crear el encabezado emite "creada" con { tipo, id }.
 *
 * Con abrirCambioCliente(tipo) solo muestra la seleccion de cliente y emite
 * "cliente" con { nit, nombre, direccion, email }; quien lo abre guarda el
 * cambio y llama a terminarCambioCliente(error).
 */
export default {
  name: "NuevaCotizacionMovilModal",
  data() {
    return {
      TIPO_FACTURA,
      TIPO_REQUERIMIENTO,
      visible: false,
      tipoFijo: false,
      modoCliente: false,
      guardando: false,
      tiendas: [],
      cajas: [],
      form: {
        tipo: TIPO_FACTURA,
        tienda: null,
        caja: null,
        tipo_pago: "CONTADO",
      },
      busquedaCliente: "",
      ultimoTextoBuscado: "",
      buscando: false,
      buscado: false,
      resultadosCliente: [],
      cliente: clienteVacio(),
      clienteSeleccionado: false,
      clienteNuevo: false,
      errores: {},
    };
  },
  computed: {
    // Requerimiento: el cliente es un supercliente creado en la tienda seleccionada
    esRequerimiento() {
      return this.form.tipo === TIPO_REQUERIMIENTO;
    },
  },
  methods: {
    /**
     * @param {String} tipo tipo de la cotizacion
     * @param {String} tienda codigo de tienda de la cotizacion (requerido en requerimiento)
     */
    abrirCambioCliente(tipo, tienda) {
      this.reiniciar();
      this.modoCliente = true;
      this.tipoFijo = true;
      this.form.tipo = tipo;
      this.form.tienda = tienda || null;
      this.visible = true;
      this.enfocarBusqueda();
    },
    guardarCliente() {
      if (this.guardando) {
        return;
      }
      if (!this.validarCliente()) {
        return;
      }
      this.guardando = true;
      const cliente = {
        nit: (this.cliente.nit || "").trim(),
        nombre: (this.cliente.nombre || "").trim(),
        direccion: (this.cliente.direccion || "").trim(),
        email: (this.cliente.email || "").trim(),
      };
      // Para factura el cliente nuevo se registra en el catalogo igual que al crear la cotizacion;
      // para requerimiento se registra como supercliente de la tienda
      const registrar =
        this.form.tipo === TIPO_FACTURA && this.clienteNuevo
          ? CotizacionMovilService.crearCliente(cliente)
          : this.esRequerimiento && this.clienteNuevo
            ? this.crearSuperClienteNuevo()
            : Promise.resolve(null);
      registrar
        .then(() => {
          if (this.clienteNuevo && this.form.tipo === TIPO_FACTURA) {
            this.clienteNuevo = false;
            this.clienteSeleccionado = true;
          }
          this.$emit("cliente", Object.assign({}, cliente, { codigo: this.cliente.codigo }));
        })
        .catch((error) => {
          this.guardando = false;
          this.errores = this.erroresBackend(error);
        });
    },
    /**
     * Resultado del cambio de cliente: sin error cierra el modal; con error lo muestra.
     */
    terminarCambioCliente(error) {
      this.guardando = false;
      if (error) {
        this.errores = this.erroresBackend(error);
        return;
      }
      this.visible = false;
    },
    abrir(tipoInicial) {
      this.reiniciar();
      this.modoCliente = false;
      this.tipoFijo = tipoInicial === TIPO_FACTURA || tipoInicial === TIPO_REQUERIMIENTO;
      if (this.tipoFijo) {
        this.form.tipo = tipoInicial;
      }
      this.visible = true;
      this.cargarTiendas();
      this.enfocarBusqueda();
    },
    cerrar() {
      this.visible = false;
      this.$emit("cerrado");
    },
    reiniciar() {
      this.guardando = false;
      this.cajas = [];
      this.form = {
        tipo: TIPO_FACTURA,
        tienda: null,
        caja: null,
        tipo_pago: "CONTADO",
      };
      this.busquedaCliente = "";
      this.ultimoTextoBuscado = "";
      this.buscando = false;
      this.buscado = false;
      this.resultadosCliente = [];
      this.cliente = clienteVacio();
      this.clienteSeleccionado = false;
      this.clienteNuevo = false;
      this.errores = {};
    },
    enfocarBusqueda() {
      this.$nextTick(() => {
        if (this.$refs.inputBuscarCliente) {
          this.$refs.inputBuscarCliente.focus();
        }
      });
    },
    seleccionarTipo(tipo) {
      if (this.form.tipo !== tipo && (this.clienteSeleccionado || this.clienteNuevo)) {
        // Factura usa clientes y requerimiento superclientes: se vuelve a buscar
        this.cambiarCliente();
      }
      this.resultadosCliente = [];
      this.buscado = false;
      this.form.tipo = tipo;
      this.errores = {};
      if (tipo === TIPO_FACTURA && this.form.tienda && this.cajas.length === 0) {
        this.cargarCajas();
      }
    },
    /**
     * Solo tiendas asignadas al usuario (activas); se preselecciona la
     * predeterminada o la unica asignada.
     */
    cargarTiendas() {
      CotizacionMovilService.tiendasUsuario()
        .then((response) => {
          const asignadas = Array.isArray(response.data) ? response.data : response.data.results || [];
          this.tiendas = asignadas
            .map((item) => item.tienda)
            .filter((tienda) => tienda && tienda.activa);
          const predeterminada = asignadas.find(
            (item) => item.predeterminada && item.tienda && item.tienda.activa
          );
          const unica = this.tiendas.length === 1 ? this.tiendas[0] : null;
          const inicial = predeterminada ? predeterminada.tienda : unica;
          if (inicial) {
            this.form.tienda = inicial.codigo;
            this.cargarCajas();
          }
        })
        .catch(() => {
          this.tiendas = [];
          Notifications.toast.error("Tiendas", "No se pudieron cargar las tiendas asignadas");
        });
    },
    onCambioTienda() {
      // El supercliente pertenece a la tienda: al cambiarla se vuelve a buscar
      if (this.esRequerimiento) {
        this.resultadosCliente = [];
        this.buscado = false;
        if (this.clienteSeleccionado) {
          this.cambiarCliente();
        }
      }
      this.cargarCajas();
    },
    cargarCajas() {
      this.form.caja = null;
      this.cajas = [];
      if (!this.form.tienda || this.form.tipo !== TIPO_FACTURA) {
        return;
      }
      const tienda = this.form.tienda;
      CotizacionMovilService.cajas(tienda)
        .then((response) => {
          if (this.form.tienda !== tienda) {
            return;
          }
          this.cajas = Array.isArray(response.data) ? response.data : response.data.results || [];
          const abiertas = this.cajas.filter((caja) => caja.esta_abierta);
          if (abiertas.length === 1) {
            this.form.caja = abiertas[0].id;
          }
        })
        .catch(() => {
          Notifications.toast.error("Cajas", "No se pudieron cargar las cajas de la tienda");
        });
    },
    buscarCliente() {
      const texto = (this.busquedaCliente || "").trim();
      if (!texto) {
        return;
      }
      if (this.esRequerimiento) {
        this.buscarSuperCliente(texto);
        return;
      }
      this.buscando = true;
      this.ultimoTextoBuscado = texto;
      CotizacionMovilService.buscarClientes(texto)
        .then((response) => {
          const data = response.data;
          this.resultadosCliente = Array.isArray(data) ? data : data.results || [];
          // Coincidencia exacta de NIT: se selecciona directo
          const exacto = this.resultadosCliente.find(
            (item) => String(item.nit).toUpperCase() === texto.toUpperCase()
          );
          if (exacto) {
            this.seleccionarCliente(exacto);
          }
        })
        .catch(() => {
          this.resultadosCliente = [];
          Notifications.toast.error("Clientes", "No se pudo consultar el catálogo de clientes");
        })
        .finally(() => {
          this.buscando = false;
          this.buscado = true;
        });
    },
    /**
     * Busca por codigo, nombre o NIT solo entre los superclientes creados en la tienda.
     */
    buscarSuperCliente(texto) {
      if (!this.form.tienda) {
        this.errores = {
          tienda: "Seleccione la tienda",
          cliente: "Seleccione la tienda",
          detail: "Seleccione la tienda antes de buscar el supercliente",
        };
        return;
      }
      const tienda = this.form.tienda;
      this.buscando = true;
      this.ultimoTextoBuscado = texto;
      CotizacionMovilService.buscarSuperClientes(texto, tienda)
        .then((response) => {
          if (this.form.tienda !== tienda) {
            return;
          }
          const data = response.data;
          this.resultadosCliente = Array.isArray(data) ? data : data.results || [];
          // Codigo exacto, o NIT exacto si solo un supercliente lo tiene (hay muchos CF)
          const buscado = texto.toUpperCase();
          const porCodigo = this.resultadosCliente.find(
            (item) => String(item.codigo).toUpperCase() === buscado
          );
          const porNit = this.resultadosCliente.filter(
            (item) => String(item.nit || "").toUpperCase() === buscado
          );
          const exacto = porCodigo || (porNit.length === 1 ? porNit[0] : null);
          if (exacto) {
            this.seleccionarCliente(exacto);
          }
        })
        .catch(() => {
          this.resultadosCliente = [];
          Notifications.toast.error("Superclientes", "No se pudo consultar el catálogo de superclientes");
        })
        .finally(() => {
          this.buscando = false;
          this.buscado = true;
        });
    },
    /**
     * Registra el supercliente nuevo en la tienda; el backend genera el codigo.
     * Despues de registrarlo ya no es "nuevo": si falla el paso siguiente no se vuelve a crear.
     */
    crearSuperClienteNuevo() {
      return CotizacionMovilService.crearSuperCliente({
        tienda: this.form.tienda,
        nit: (this.cliente.nit || "").trim(),
        nombre: (this.cliente.nombre || "").trim(),
        direccion: (this.cliente.direccion || "").trim(),
      }).then((response) => {
        this.cliente.codigo = response.data.codigo;
        this.clienteNuevo = false;
        this.clienteSeleccionado = true;
      });
    },
    seleccionarCliente(item) {
      this.cliente = {
        codigo: item.codigo || "",
        nit: item.nit,
        nombre: item.nombre,
        direccion: item.direccion || "",
        email: item.email || "",
      };
      this.clienteSeleccionado = true;
      this.clienteNuevo = false;
      this.errores = {};
    },
    cambiarCliente() {
      this.clienteSeleccionado = false;
      this.clienteNuevo = false;
      this.cliente = clienteVacio();
      this.enfocarBusqueda();
    },
    ingresarClienteNuevo() {
      this.clienteNuevo = true;
      this.cliente = clienteVacio();
      const texto = (this.busquedaCliente || "").trim();
      // Si lo buscado parece un NIT se precarga
      if (texto && /^[0-9kK-]+$/.test(texto)) {
        this.cliente.nit = texto.toUpperCase();
      }
    },
    cancelarClienteNuevo() {
      this.clienteNuevo = false;
      this.cliente = clienteVacio();
      this.errores = {};
      this.enfocarBusqueda();
    },
    validar() {
      const errores = {};
      if (!this.form.tienda) {
        errores.tienda = "Seleccione la tienda";
      }
      if (this.form.tipo === TIPO_FACTURA && !this.form.caja) {
        errores.caja = "Seleccione una caja abierta";
      }
      Object.assign(errores, this.erroresCliente());
      this.errores = errores;
      return Object.keys(errores).length === 0;
    },
    validarCliente() {
      this.errores = this.erroresCliente();
      return Object.keys(this.errores).length === 0;
    },
    erroresCliente() {
      const errores = {};
      if (!this.clienteSeleccionado && !this.clienteNuevo) {
        errores.cliente = this.esRequerimiento
          ? "Busque o ingrese el supercliente"
          : "Busque o ingrese el cliente";
      }
      if (this.esRequerimiento && this.modoCliente && !this.form.tienda) {
        errores.cliente = "No se identificó la tienda de la cotización";
        errores.detail = errores.cliente;
      }
      if (this.esRequerimiento && this.clienteSeleccionado && !this.cliente.codigo) {
        errores.cliente = "Seleccione un supercliente de la tienda";
        errores.detail = errores.cliente;
      }
      if (this.clienteNuevo) {
        if (!(this.cliente.nit || "").trim()) {
          errores.nit = "Ingrese el NIT";
        }
        if (!(this.cliente.nombre || "").trim()) {
          errores.nombre = "Ingrese el nombre";
        }
        if (!(this.cliente.direccion || "").trim()) {
          errores.direccion = "Ingrese la dirección";
        }
      }
      return errores;
    },
    /**
     * Convierte la respuesta de error del backend en { campo: mensaje }.
     */
    erroresBackend(error) {
      const data = error && error.response ? error.response.data : null;
      if (!data) {
        return { detail: "No se pudo completar la operación" };
      }
      if (typeof data === "string") {
        return { detail: data };
      }
      if (Array.isArray(data)) {
        return { detail: data.join(" ") };
      }
      const errores = {};
      Object.keys(data).forEach((campo) => {
        const valor = data[campo];
        errores[campo] = Array.isArray(valor) ? valor.join(" ") : String(valor);
      });
      if (!errores.detail) {
        errores.detail = Object.keys(errores)
          .map((campo) => `${campo}: ${errores[campo]}`)
          .join(" | ");
      }
      return errores;
    },
    crear() {
      if (this.guardando || !this.validar()) {
        return;
      }
      this.guardando = true;
      const tipo = this.form.tipo;
      const registrarCliente =
        tipo === TIPO_FACTURA && this.clienteNuevo
          ? CotizacionMovilService.crearCliente({
            nit: this.cliente.nit.trim(),
            nombre: this.cliente.nombre.trim(),
            direccion: this.cliente.direccion.trim(),
            email: (this.cliente.email || "").trim(),
          })
          : tipo === TIPO_REQUERIMIENTO && this.clienteNuevo
            ? this.crearSuperClienteNuevo()
            : Promise.resolve(null);

      registrarCliente
        .then(() => {
          // Despues de registrarlo ya no es "nuevo": si falla el encabezado no se vuelve a crear
          if (this.clienteNuevo && tipo === TIPO_FACTURA) {
            this.clienteNuevo = false;
            this.clienteSeleccionado = true;
          }
          return CotizacionMovilService.crear(tipo, this.payloadEncabezado(tipo));
        })
        .then((response) => {
          this.visible = false;
          Notifications.toast.success("Cotización", "Cliente listo, escanee los productos");
          this.$emit("creada", { tipo: tipo, id: response.data.id });
        })
        .catch((error) => {
          this.errores = this.erroresBackend(error);
        })
        .finally(() => {
          this.guardando = false;
        });
    },
    payloadEncabezado(tipo) {
      const nit = (this.cliente.nit || "").trim();
      const nombre = (this.cliente.nombre || "").trim();
      const direccion = (this.cliente.direccion || "").trim();
      if (tipo === TIPO_FACTURA) {
        // Mismos campos que el modal actual de cotizaciones a clientes
        return {
          tienda: this.form.tienda,
          caja: this.form.caja,
          tipo_pago: this.form.tipo_pago,
          cliente_nit: nit,
          cliente_nombre: nombre,
          cliente_direccion: direccion,
          lista_precio: null,
          estado: "A",
        };
      }
      // Vence en 5 dias por defecto (lo asigna el backend).
      // nit, nombre y direccion los copia el backend del supercliente.
      return {
        tienda: this.form.tienda,
        super_cliente: this.cliente.codigo,
        email: (this.cliente.email || "").trim() || null,
        fecha_vence: null,
      };
    },
  },
};
</script>

<style scoped>
.cr-modal-fondo {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1050;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  overflow-y: auto;
  padding: 24px 12px;
}

.cr-modal {
  background-color: #fff;
  border-radius: 8px;
  width: 100%;
  max-width: 640px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
}

.cr-modal-header,
.cr-modal-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
}

.cr-modal-header {
  border-bottom: 1px solid #e9ecef;
}

.cr-modal-footer {
  border-top: 1px solid #e9ecef;
}

/* Margenes en lugar de gap: algunas handheld usan WebView antiguos */
.cr-modal-footer .btn {
  flex: 1;
  min-height: 46px;
}

.cr-modal-footer .btn + .btn {
  margin-left: 8px;
}

.cr-modal-body {
  padding: 16px;
  overflow-y: auto;
}

.cr-label {
  font-weight: bold;
  font-size: 0.85rem;
  margin-bottom: 4px;
}

.cr-tipos {
  display: flex;
}

.cr-tipos .btn {
  flex: 1;
  min-height: 52px;
  font-weight: bold;
}

.cr-tipos .btn + .btn {
  margin-left: 8px;
}

.cr-resultados {
  margin-top: 8px;
  max-height: 240px;
  overflow-y: auto;
  border: 1px solid #dee2e6;
  border-radius: 6px;
}

.cr-resultado {
  display: block;
  width: 100%;
  text-align: left;
  background: #fff;
  border: 0;
  border-bottom: 1px solid #f1f1f1;
  padding: 10px 12px;
  min-height: 48px;
}

.cr-resultado:active,
.cr-resultado:hover {
  background-color: #eef4ff;
}

.cr-cliente-sel {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  border: 1px solid #b8daff;
  background-color: #f0f7ff;
  border-radius: 6px;
}

.form-control {
  min-height: 44px;
}

/* En moviles el modal ocupa toda la pantalla */
@media (max-width: 767px) {
  .cr-modal-fondo {
    padding: 0;
  }

  .cr-modal {
    min-height: 100%;
    max-width: none;
    border-radius: 0;
  }

  .cr-modal-body {
    flex: 1;
  }

  .cr-modal-footer {
    position: sticky;
    bottom: 0;
    background-color: #fff;
  }
}
</style>
